import React from 'react';
import { Circle, Close } from '@mui/icons-material';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

interface NotificationCardProps {
  notification: {
    read_on?: number | null;
    seen_on?: number;
    created_on: number;
    message: {
      header?: string;
      text: string;
      subtext?: { text: string };
      actions?: Array<{ name: string; url: string; open_in_new_tab?: boolean }>;
    };
  };
  onArchive: () => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onArchive
}) => {
  return (
    <Stack
      sx={{
        borderTop: '1px solid var(--palette-semantic-border-tertiary)',
        padding: '16px',
        boxSizing: 'border-box'
      }}
      bgcolor={
        !notification.read_on
          ? '#EEF4F9'
          : 'var(--palette-semantic-background-base)'
      }
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        pb="4px"
        alignItems="flex-start"
      >
        <Stack flex={1} minWidth={0}>
          <Typography
            color="var(--palette-semantic-text-primary)"
            fontWeight={500}
            fontSize="16px"
            display="flex"
            sx={{ alignItems: 'flex-start' }}
          >
            <Circle
              htmlColor={
                notification.seen_on
                  ? 'var(--palette-semantic-icon-disabled)'
                  : 'var(--palette-semantic-icon-accent)'
              }
              sx={{
                width: '6px',
                height: '6px',
                marginRight: '8px',
                marginTop: '8px',
                flexShrink: 0
              }}
            />
            {notification.message.header}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" flexShrink={0}>
          <Typography
            color="var(--palette-semantic-text-caption)"
            fontWeight={400}
            fontSize="10px"
          >
            {dayjs(notification.created_on).format('MMM D h:mm A')}
          </Typography>
          <IconButton onClick={onArchive} size="small" sx={{ mr: '-5px' }}>
            <Close
              htmlColor="var(--palette-semantic-icon-tertiary)"
              sx={{ width: 16, height: 16 }}
            />
          </IconButton>
        </Stack>
      </Stack>
      <Typography
        color="var(--palette-semantic-text-primary)"
        fontSize="14px"
        fontWeight={400}
      >
        {notification.message.text}
      </Typography>
      {notification.message?.subtext && (
        <Typography
          color="var(--palette-semantic-text-secondary)"
          fontSize="12px"
          fontWeight={400}
          mt={1}
        >
          {notification.message.subtext.text}
        </Typography>
      )}
      {notification.message?.actions &&
        notification.message.actions.length > 0 && (
          <Stack direction="row" gap={2} mt={1}>
            {notification.message.actions.map((action, index) => (
              <Button
                key={index}
                size="medium"
                variant="contained"
                sx={{ marginTop: '8px', cursor: 'pointer' }}
                color={index === 0 ? 'primary' : 'secondary'}
                onClick={() => {
                  if (action.open_in_new_tab) {
                    window.open(action.url, '_blank');
                  } else {
                    window.location.href = action.url;
                  }
                }}
              >
                {action.name}
              </Button>
            ))}
          </Stack>
        )}
    </Stack>
  );
};

export default NotificationCard;
